/* PARATITO B2B attribution and Google Ads lead tracking.
 * Google loads only when Shopify permits marketing AND data sharing.
 * A lead requires Shopify's native success markup and the matching receipt. */
(() => {
  'use strict';
  if (window.ParatitoB2BTracking) return;
  if (window.parent !== window) return;
  const cfg = Object.assign({ eventsEnabled: true, adsTagId: 'AW-369442876', adsSendTo: 'AW-369442876/lxPTCP-BwvgcELyAlbAB' }, window.PARATITO_B2B_TRACKING_CONFIG || {});
  const key = 'paratito-b2b-attribution/v1';
  const sentKey = 'paratito-b2b-sent/v1';
  const ttl = 90 * 86400000;
  const params = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_id', 'utm_term', 'utm_content', 'gclid', 'gbraid', 'wbraid', 'device', 'matchtype'];
  let state = null, confirmation = null, tagStatus = 'idle', configured = false, tagError = false;
  let lastConsent = '', layerName = 'dataLayer';
  const memorySent = new Set();
  const permission = purpose => {
    try {
      const api = window.Shopify?.customerPrivacy;
      const method = purpose === 'analytics' ? 'analyticsProcessingAllowed' : purpose === 'sharing' ? 'saleOfDataAllowed' : 'marketingAllowed';
      return typeof api?.[method] === 'function' ? api[method]() === true : null;
    } catch { return null; }
  };
  const allowed = purpose => permission(purpose) === true;
  const adsAllowed = () => allowed('marketing') && allowed('sharing');
  const read = storageKey => {
    try { return JSON.parse(localStorage.getItem(storageKey)); } catch { return null; }
  };
  const write = (storageKey, value) => {
    try { value === null ? localStorage.removeItem(storageKey) : localStorage.setItem(storageKey, JSON.stringify(value)); } catch {}
  };
  const clean = (value, max = 200) => {
    if (typeof value !== 'string' || value.length > max || /[<>\u0000-\u001f\u007f@{}]/.test(value)) return '';
    return value.trim();
  };
  const path = value => {
    const result = clean(value, 300);
    // Public Shopify routes only. Never capture queries, fragments or account URLs.
    return /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:$|(?:pages|products|collections|blogs)\/[a-z0-9_\/-]+$)/i.test(result) ? result : '';
  };
  const domain = value => {
    try { const url = new URL(value); return ['https:', 'http:'].includes(url.protocol) ? url.hostname.toLowerCase() : ''; } catch { return ''; }
  };
  const sanitizeTouch = value => {
    if (!value || typeof value !== 'object' || !Number.isFinite(value.at) || value.at > Date.now() + 60000 || Date.now() - value.at > ttl) return null;
    const p = {};
    for (const name of params) {
      const v = clean(value.parameters?.[name], name === 'gclid' || name === 'gbraid' || name === 'wbraid' ? 512 : 200);
      if (!v) continue;
      if (['gclid', 'gbraid', 'wbraid'].includes(name) && !/^[A-Za-z0-9_.~-]+$/.test(v)) continue;
      p[name] = v;
    }
    const referrer = /^[a-z0-9.-]{1,253}$/.test(value.referrer_domain || '') ? value.referrer_domain : '';
    const hasClick = !!(p.gclid || p.gbraid || p.wbraid);
    const evidence = hasClick ? 'google_click_id' : p.utm_source ? 'utm' : referrer ? 'referrer' : 'none';
    return { at: value.at, landing_path: path(value.landing_path), referrer_domain: referrer, parameters: p, source: p.utm_source || (hasClick ? 'google' : referrer || 'direct'), medium: p.utm_medium || (hasClick ? 'cpc' : referrer ? 'referral' : '(none)'), evidence };
  };
  const capture = () => {
    if (!allowed('marketing')) return null;
    const p = {}, query = new URLSearchParams(location.search);
    for (const name of params) {
      const v = clean(query.get(name), ['gclid', 'gbraid', 'wbraid'].includes(name) ? 512 : 200);
      if (v && (!['gclid', 'gbraid', 'wbraid'].includes(name) || /^[A-Za-z0-9_.~-]+$/.test(v))) p[name] = v;
    }
    const referrer = domain(document.referrer);
    const internal = [location.hostname, 'paratito.com', 'www.paratito.com', 'khaki-firsthand-nonlinearprogramming.replit.app'].includes(referrer);
    const hasClick = !!(p.gclid || p.gbraid || p.wbraid);
    const evidence = hasClick ? 'google_click_id' : p.utm_source ? 'utm' : referrer && !internal ? 'referrer' : 'none';
    return { at: Date.now(), landing_path: path(location.pathname), referrer_domain: internal ? '' : referrer, parameters: p, source: p.utm_source || (hasClick ? 'google' : evidence === 'referrer' ? referrer : 'direct'), medium: p.utm_medium || (hasClick ? 'cpc' : evidence === 'referrer' ? 'referral' : '(none)'), evidence };
  };
  const update = () => {
    if (!allowed('marketing')) { state = null; write(key, null); return; }
    if (!path(location.pathname)) return;
    const saved = read(key) || state;
    const first = sanitizeTouch(saved?.first_touch), savedLast = sanitizeTouch(saved?.last_non_direct_touch);
    const last = savedLast?.evidence !== 'none' ? savedLast : null;
    const current = capture();
    if (!current) return;
    // Same in-page consent refresh must not manufacture a new acquisition touch.
    const previousCurrent = sanitizeTouch(state?.current_touch);
    const comparable = touch => touch && JSON.stringify([touch.landing_path, touch.referrer_domain, touch.parameters, touch.evidence]);
    const currentTouch = comparable(previousCurrent) === comparable(current) ? previousCurrent : current;
    state = { schema_version: 1, first_touch: first || currentTouch, last_non_direct_touch: currentTouch.evidence !== 'none' ? currentTouch : last, current_touch: currentTouch };
    write(key, state);
  };
  const payload = () => {
    if (!allowed('marketing')) return null;
    if (!state) update();
    return state ? JSON.parse(JSON.stringify(state)) : null;
  };
  const contactFields = () => {
    const attribution = payload();
    const touch = attribution?.last_non_direct_touch || attribution?.first_touch;
    return {
      'contact[Herkunft]': touch ? `${touch.source} / ${touch.medium}` : 'Unbekannt (Tracking-Einwilligung fehlt)',
      'contact[Tracking-Consent]': JSON.stringify({ checked_at: new Date().toISOString(), marketing_allowed: permission('marketing'), analytics_processing_allowed: permission('analytics'), sale_of_data_allowed: permission('sharing'), null_means: 'unknown_not_allowed', attribution_status: attribution ? 'observed_client_metadata' : 'unknown_consent_not_allowed' }),
      ...(attribution ? {
        'contact[Tracking-Herkunft]': JSON.stringify(attribution),
        'contact[Quelle-Nachweis]': touch.evidence,
        ...(touch.parameters.utm_campaign ? { 'contact[Kampagne]': touch.parameters.utm_campaign } : {}),
        ...(touch.parameters.utm_term ? { 'contact[Gebuchtes Keyword]': touch.parameters.utm_term } : {})
      } : {})
    };
  };
  const applyToForm = form => {
    if (!form) return;
    const container = form.querySelector('[data-pt-attribution-fields]') || document.createElement('div');
    container.setAttribute('data-pt-attribution-fields', ''); container.hidden = true;
    container.replaceChildren();
    for (const [name, value] of Object.entries(contactFields())) {
      const input = document.createElement('input'); input.type = 'hidden'; input.name = name; input.value = value; container.append(input);
    }
    if (!container.parentNode) form.append(container);
  };
  const syncConsent = () => {
    if (typeof window.gtag !== 'function' || tagStatus === 'idle') return;
    const consent = {
      ad_storage: allowed('marketing') ? 'granted' : 'denied',
      analytics_storage: allowed('analytics') ? 'granted' : 'denied',
      ad_user_data: adsAllowed() ? 'granted' : 'denied',
      ad_personalization: adsAllowed() ? 'granted' : 'denied'
    };
    const encoded = JSON.stringify(consent);
    if (encoded !== lastConsent) { window.gtag('consent', 'update', consent); lastConsent = encoded; }
  };
  const queueConfig = () => {
    if (!configured) {
      const existingConfig = Array.from(window[layerName] || []).some(command => command?.[0] === 'config' && command?.[1] === cfg.adsTagId);
      if (!existingConfig) window.gtag('config', cfg.adsTagId, { send_page_view: false, allow_ad_personalization_signals: false });
      configured = true;
    }
  };
  const configureTag = () => {
    if (tagStatus !== 'loaded' || !adsAllowed()) return false;
    syncConsent();
    queueConfig();
    return true;
  };
  const ensureTag = () => {
    if (!cfg.eventsEnabled || !adsAllowed() || !path(location.pathname) || tagError) return;
    if (tagStatus === 'loaded') { configureTag(); return; }
    if (tagStatus === 'loading' || tagStatus === 'waiting_existing') { syncConsent(); return; }
    // Honor the one data layer already selected by a top-level Google install.
    let existingLoader = null;
    for (const script of Array.from(document.scripts || [])) {
      try {
        const url = new URL(script.src);
        if (url.hostname === 'www.googletagmanager.com' && ['/gtag/js', '/gtm.js'].includes(url.pathname)) {
          const name = url.searchParams.get('l');
          if (name && /^[A-Za-z_$][\w$]{0,63}$/.test(name)) layerName = name;
          if (url.pathname === '/gtag/js') { existingLoader = script; break; }
        }
      } catch {}
    }
    const hadTag = typeof window.gtag === 'function';
    window[layerName] = window[layerName] || [];
    if (!hadTag) window.gtag = function () { window[layerName].push(arguments); };
    tagStatus = 'loading';
    if (!hadTag) {
      window.gtag('consent', 'default', { ad_storage: 'denied', analytics_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
      window.gtag('js', new Date());
    }
    syncConsent();
    if (existingLoader) {
      tagStatus = 'waiting_existing';
      const loaded = () => { tagStatus = 'loaded'; if (configureTag()) flush(); };
      existingLoader.addEventListener('load', loaded, { once: true });
      existingLoader.addEventListener('error', () => { tagStatus = 'failed'; tagError = true; }, { once: true });
      // Official Google Ads get callback also handles an already loaded library.
      // Discard its value: only successful command processing proves readiness.
      queueConfig();
      window.gtag('get', cfg.adsTagId, 'gclid', () => loaded());
      return;
    }
    const script = document.createElement('script');
    script.async = true;
    script.referrerPolicy = 'strict-origin';
    script.setAttribute('data-pt-b2b-google-tag', '');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(cfg.adsTagId)}${layerName !== 'dataLayer' ? `&l=${encodeURIComponent(layerName)}` : ''}`;
    script.onload = () => { tagStatus = 'loaded'; if (configureTag()) flush(); };
    script.onerror = () => { tagStatus = 'failed'; tagError = true; };
    try { (document.head || document.documentElement).appendChild(script); }
    catch { tagStatus = 'failed'; tagError = true; }
  };
  const validReceipt = () => {
    if (!document.querySelector('#FirmenkundenForm [data-b2b-contact-success]')) return null;
    try {
      const saved = JSON.parse(sessionStorage.getItem('paratito-inquiry-separate-v1'));
      const receipt = saved?.receipt;
      const age = Date.now() - Date.parse(receipt?.submittedAt);
      if (saved?.sending !== true || !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(receipt?.requestId || '') || !/^PT-\d{8}-[0-9A-F]{8}$/.test(receipt?.inquiryId || '') || !Number.isFinite(age) || age < -60000 || age > 86400000) return null;
      return receipt;
    } catch { return null; }
  };
  const sendOnce = (destination, eventName) => {
    if (!confirmation || !cfg.eventsEnabled || !destination || !adsAllowed() || !configureTag() || typeof window.gtag !== 'function') return false;
    const receipt = validReceipt();
    if (!receipt || receipt.inquiryId !== confirmation.inquiryId) return false;
    const eventKey = `${eventName}:${destination}:${confirmation.inquiryId}`;
    const sent = read(sentKey) || {};
    if (memorySent.has(eventKey) || Number.isFinite(sent[eventKey]) && Date.now() - sent[eventKey] < ttl) return true;
    const safeEvent = { send_to: destination, transaction_id: confirmation.inquiryId };
    if (eventName === 'conversion') { safeEvent.value = 0; safeEvent.currency = 'EUR'; }
    safeEvent.allow_ad_personalization_signals = false;
    // No email, name, company, phone, notes, file tokens or click IDs in event payload.
    try { window.gtag('event', eventName, safeEvent); } catch { return false; }
    memorySent.add(eventKey);
    const bounded = Object.fromEntries(Object.entries(sent).filter(([,at]) => Number.isFinite(at) && Date.now() - at < ttl).slice(-99));
    bounded[eventKey] = Date.now(); write(sentKey, bounded);
    return true;
  };
  const flush = () => {
    if (!confirmation) return;
    ensureTag();
    sendOnce(cfg.adsSendTo, 'conversion');
  };
  const confirmedInquiry = (inquiryId, submittedAt) => {
    const receipt = validReceipt();
    if (!receipt || receipt.inquiryId !== inquiryId || receipt.submittedAt !== submittedAt) return false;
    confirmation = { inquiryId }; flush(); return true;
  };
  const checkNativeSuccess = () => {
    if (confirmation) return;
    // Success must be rendered by Shopify, never inferred from ?contact_posted=true.
    // This fallback does not depend on the separate Replit catalog service loading.
    const receipt = validReceipt();
    if (receipt) confirmedInquiry(receipt.inquiryId, receipt.submittedAt);
  };
  const refresh = () => {
    update();
    syncConsent();
    ensureTag();
    checkNativeSuccess();
    document.querySelectorAll('#FirmenkundenForm').forEach(applyToForm);
    flush();
  };
  window.ParatitoB2BTracking = { contactFields, applyToForm, payload, confirmedInquiry, status: () => ({ tag: tagStatus, configured, marketingAllowed: permission('marketing'), sharingAllowed: permission('sharing') }) };
  document.addEventListener('visitorConsentCollected', refresh);
  document.addEventListener('submit', event => { if (event.target?.id === 'FirmenkundenForm') applyToForm(event.target); }, true);
  document.addEventListener('DOMContentLoaded', refresh, { once: true });
  if (window.Shopify?.customerPrivacy) refresh();
  else if (typeof window.Shopify?.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => { if (!error) refresh(); });
  }
})();
